import React from 'react';
import { Query } from 'react-apollo';
import { styled } from 'linaria/react';
import BlogHeader from './BlogHeader';
import Head from '@jetshop/core/components/Head';
import blogArticleQuery from './BlogArticleQuery.gql';
import blogArticleDetailedQuery from './BlogArticleDetailedQuery.gql';
import { ContentPadding } from '../Layout/Content/Content';
import { PagePadding } from '../Layout/MaxWidth';
import BlogArticleLoadingState from './BlogArticleLoadingState';
import { theme } from '../Theming/Theming';
import BlogItemList from './BlogItemList';
import t from '@jetshop/intl';
import { ProductListHeader } from '../ProductPage/StyledComponents';
import { useLocation } from 'react-router';
import { useBlog } from './hook/useBlog';
import TextContent from '../ui/TextContent';
import { RowProducts } from '../ui/ProductRow/RowProducts';

export const BlogArticleMaxWidth = styled('div')`
  max-width: 800px;
  margin: 0 auto;
`;

const BlogArticleWrapper = styled('div')`
  margin-top: 2.5rem;
  ${ContentPadding}
  .blog-article-main-area {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  .blog-article-content {
    h2,
    h3,
    h4,
    h5 {
      margin: 1.5em 0 1em;
    }
    p {
      line-height: 1.5em;
      margin-bottom: 1rem;
    }
    img {
      margin: 1.5rem 0;
    }
  }
  .blog-article-products {
    ${theme.below.lg} {
      margin-top: 2rem;
    }
  }
  &[data-detailedview='true'] {
    max-width: none;
    width: 100%;
    .article-inner {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      ${theme.above.lg} {
        flex-direction: row;
        .blog-header-wrapper {
          width: 35%;
        }
        .blog-article-products {
          width: 65%;
          padding: 0 3rem;
        }
      }
    }
  }
`;

const StyledProductListHeader = styled(ProductListHeader)`
  margin-top: 4rem;
`;

const BlogArticle = ({ result, configObject, detailedProduct }) => {
  const { data } = result;
  const category = (data && data.route && data.route.object) || null;

  const { articles, menuCategories } = useBlog({
    ...configObject,
    parentId: category && category.parentId,
    exclude: [category && category.id]
  });

  if (!data || !category) {
    return <BlogArticleLoadingState />;
  }

  const { route } = data;
  const breadcrumbProps = route
    ? {
        breadcrumbs: route.breadcrumbs,
        parents: route.parents
      }
    : null;

  const parentCategory = menuCategories.filter(cat => {
    return cat.id === category.parentId;
  });

  return (
    <>
      {category.head && <Head data={category} route={route} />}
      <BlogHeader
        category={category}
        parentCategory={parentCategory && parentCategory[0]}
        breadcrumbProps={breadcrumbProps}
      />
      <PagePadding style={{ marginBottom: '5rem' }}>
        <BlogArticleMaxWidth>
          <BlogArticleWrapper
            data-hasproducts={
              category.products && category.products.result.length > 0
                ? true
                : false
            }
            data-detailedview={detailedProduct ? true : false}
          >
            <div className="article-inner">
              {category.content && <TextContent content={category.content} />}
            </div>
          </BlogArticleWrapper>
        </BlogArticleMaxWidth>
        {category.products && category.products.result && (
          <div className="blog-article-products">
            <RowProducts category={category} slider={true} />
          </div>
        )}
        <StyledProductListHeader>
          {t('You will also like this')}
        </StyledProductListHeader>
        <BlogItemList categories={articles} blogPage={false} />
      </PagePadding>
    </>
  );
};

const ArticleComponent = ({ configObject, detailedProduct = false }) => {
  const location = useLocation();
  const query = detailedProduct ? blogArticleDetailedQuery : blogArticleQuery;
  return (
    <Query query={query} variables={{ path: location.pathname }}>
      {result => (
        <BlogArticle
          configObject={configObject}
          result={result}
          detailedProduct={detailedProduct}
        />
      )}
    </Query>
  );
};

export default ArticleComponent;
